import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import {useNavigate} from 'react-router-dom'
import {GET, DELETE} from '../../../services/api'
import useModals from '../../../hooks/useModals'
import {ModalDelete} from '../../../components/Modals/General/ModalDelete/ModalDelete'
import {Loading} from '../../../components/Loading/Loading'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {i18n} from '../../../translate/i18n'
import {useSwitchStatus} from '../../../components/Datatable/SwitchStatus'
import Swal from 'sweetalert2'

export interface Store {
  id: number
  pluginspaceId: number
  link: string
  description: string
  code: string
  status: boolean
  paymentIntegrated: boolean
  created: string
  updated: string
  order: number
  img: string | null
  vinculationType: VinculationType

  // campos do pluginspace
  name: string
  cnpj: string
  type: string
  pluginspace?: Pluginspace
}

export interface Pluginspace {
  id: number
  name: string
  corporateName: string
  cnpj: string
  hash: string
  subdomain: string
  type: string
  imageLogin: string | null
  permission: any | null
  content: {
    theme: {
      admin: string
      logo: string
    }
  }
  skinId: number
  image: string | null
  terms: boolean
  appLogin: boolean
  gmt: string
  translate: boolean
  createdUserId: number
  confirmationDateTime: string | null
  confirmationHash: string | null
  status: boolean
  created: string
  updated: string
  deleted: string | null
  removed: number
  parent: number
  storeCode: string
  defaultLanguage: string
  lead: number
  category: string | null
  defaultCurrency: string
  resaleId: number | null
  resellerId: number | null
  emailContact: string | null
  phoneContact: string | null
  wholeSaler: boolean
}

export enum VinculationType {
  DEFAULT = 'default',
  DYNAMIC = 'dynamic',
}

const HotelStoresList: FC = () => {
  const [loading, setLoading] = useState(false)
  const [children, setChildren] = useState<Store[]>([])
  const navigate = useNavigate()
  const {switchStatus} = useSwitchStatus()
  const {showDeleteModal} = useModals()
  const [id, setId] = useState<number | null>(null)

  const getStores = async () => {
    try {
      const response = await GET('/stores-hotel')

      if (response) {
        const mapped: Store[] = response.data.map((store: Store) => ({
          id: store.id,
          pluginspaceId: store.pluginspaceId,
          link: store.link,
          description: store.description,
          code: store.code,
          status: store.status,
          paymentIntegrated:
            store.paymentIntegrated === true
              ? i18n.t('stores_pages.table.status_active')
              : i18n.t('stores_pages.table.status_inactive'),
          created: store.created,
          updated: store.updated,
          order: store.order,
          img: store.img,
          vinculationType:
            store.vinculationType === VinculationType.DEFAULT
              ? i18n.t('stores_pages.table.vinculation_default')
              : store.vinculationType === VinculationType.DYNAMIC
              ? i18n.t('stores_pages.table.vinculation_dynamic')
              : store.vinculationType ?? VinculationType.DEFAULT,

          // mapeando de dentro do pluginspace
          name: store.pluginspace?.name ?? '',
          cnpj: store.pluginspace?.cnpj ?? '',
          type: store.pluginspace?.type ?? '',
        }))

        setChildren(mapped)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const init = async () => {
    setLoading(true)
    await getStores()
    setLoading(false)
  }

  const deleteStore = async () => {
    try {
      setLoading(true)
      const res = await DELETE(`/stores-hotel/${id}`)
      if (res && res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('stores_pages.success')),
          text: String(i18n.t('stores_pages.delete_success')),
          showConfirmButton: true,
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('stores_pages.error')),
          text: String(i18n.t('stores_pages.delete_error')),
          showConfirmButton: true,
        })
      }
    } catch (e) {
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('stores_pages.error')),
        text: String(i18n.t('stores_pages.delete_error')),
        showConfirmButton: true,
      })
    } finally {
      setLoading(false)
      await getStores()
    }
  }

  useEffect(() => {
    init()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('stores_pages.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('stores_pages.subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('stores_pages.create')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/configuracoes/stores/create')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              {children.length === 0 ? (
                <div className='text-center text-muted'>{i18n.t('stores_pages.no_results')}</div>
              ) : (
                <Datatable
                  data={children}
                  headers={[
                    {key: i18n.t('stores_pages.table.code'), value: 'code', type: 'default'},
                    {key: i18n.t('stores_pages.table.image'), value: 'img', type: 'image'},
                    {key: i18n.t('stores_pages.table.name'), value: 'name', type: 'default'},
                    {
                      key: i18n.t('stores_pages.table.vinculation'),
                      value: 'vinculationType',
                      type: 'default',
                    },
                    {key: i18n.t('stores_pages.table.type'), value: 'type', type: 'default'},
                    {
                      key: i18n.t('stores_pages.table.order'),
                      value: 'order',
                      type: 'default',
                    },
                    {
                      key: i18n.t('stores_pages.table.status'),
                      value: 'status',
                      type: 'check',
                      action: (item: any) =>
                        switchStatus({endpoint: 'stores-hotel', Id: item.id, status: item.status}),
                    },
                    {
                      key: i18n.t('stores_pages.table.payment_integrated'),
                      value: 'paymentIntegrated',
                      type: 'default',
                    },
                    {key: i18n.t('stores_pages.table.actions'), value: 'acoes', type: ''},
                  ]}
                  options={[
                    {
                      icon: 'fas fa-pen',
                      action: (item) => {
                        navigate(`/hospitality/configuracoes/stores/edit/${item.id}`)
                      },
                      title: i18n.t('stores_pages.actions.edit'),
                    },
                    {
                      icon: 'fas fa-trash',
                      action: (item) => {
                        showDeleteModal(i18n.t('stores_pages.actions.delete_confirm'))
                        setId(item.id)
                      },
                      title: i18n.t('stores_pages.actions.delete'),
                    },
                  ]}
                  color='primary'
                />
              )}
            </div>
          </div>
          <ModalDelete confirmAction={() => deleteStore()} />
        </div>
      )}
    </>
  )
}

const HotelStores: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('permission_routes.dashboard'), path: 'dashboard', isActive: false},
          {title: i18n.t('permission_routes.healthcare'), path: 'hospitality', isActive: false},
          {
            title: i18n.t('stores_pages.title'),
            path: 'hospitality/configuracoes/stores',
            isActive: true,
          },
        ]}
      >
        {i18n.t('stores_pages.title')}
      </PageTitle>
      <HotelStoresList />
    </>
  )
}

export {HotelStores}
